import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { patchField, type DocumentResultResponse } from '../api';
import { reviewerQueryKeys } from './useDocumentResult';
import { useToastStore } from '../../../stores/toastStore';

export interface FieldEditState {
  /** Local override applied optimistically on top of the server value. */
  value: unknown;
  /** True after a successful PATCH — the UI flips the score badge to a pencil. */
  manuallyEdited: boolean;
  /** True while the PATCH is in flight. */
  saving: boolean;
}

export interface UseFieldEditResult {
  edits: Record<string, FieldEditState>;
  commit: (jobId: string, fieldName: string, nextValue: unknown) => Promise<void>;
  /** Reset all local edits (e.g. after the parent triggers a refetch). */
  reset: () => void;
}

/**
 * Tracks inline edits per field with optimistic semantics and a real
 * `PATCH /documents/{id}/fields/{name}` (Wave 6.B) call. On success the
 * refreshed result is written into the React Query cache so the rest of the
 * UI picks up the new confidence numbers without a round-trip. On error the
 * local override is rolled back and a toast surfaces the failure.
 */
export function useFieldEdit(): UseFieldEditResult {
  const [edits, setEdits] = useState<Record<string, FieldEditState>>({});
  const pushToast = useToastStore((state) => state.push);
  const queryClient = useQueryClient();

  const commit = useCallback(
    async (jobId: string, fieldName: string, nextValue: unknown): Promise<void> => {
      setEdits((prev) => ({
        ...prev,
        [fieldName]: {
          value: nextValue,
          manuallyEdited: prev[fieldName]?.manuallyEdited ?? false,
          saving: true,
        },
      }));

      try {
        const refreshed: DocumentResultResponse = await patchField(jobId, fieldName, {
          value: nextValue,
        });
        // Replace the cached result so FieldRow re-renders with the new confidence numbers
        // without having to re-fetch. Match the key used by useDocumentResult exactly.
        queryClient.setQueryData(reviewerQueryKeys.result(jobId), refreshed);
        setEdits((prev) => ({
          ...prev,
          [fieldName]: {
            value: nextValue,
            manuallyEdited: true,
            saving: false,
          },
        }));
        pushToast({
          tone: 'success',
          title: 'Campo actualizado',
          description: 'La corrección manual se ha guardado.',
          durationMs: 3_500,
        });
      } catch (cause) {
        // Rollback the optimistic override so the UI returns to the server value.
        setEdits((prev) => {
          const next = { ...prev };
          delete next[fieldName];
          return next;
        });
        pushToast({
          tone: 'error',
          title: 'No se pudo guardar el cambio',
          description: cause instanceof Error ? cause.message : 'Inténtalo de nuevo.',
        });
      }
    },
    [pushToast, queryClient],
  );

  const reset = useCallback(() => {
    setEdits({});
  }, []);

  return { edits, commit, reset };
}
